// 定义蓝牙设备
import { Node } from "@antv/x6";
import { DeviceType, HALInterface, HALFrameType, IHALInterface, IHALDevice, ISimulatorDevice } from "./Base";
import { IOT_EVENT_LEVEL, IOT_EVNET } from "./IOT";

// 定义蓝牙设备基本信息
export interface BluetoothInfo {
    deviceName: string; // 设备名称
    deviceType: DeviceType; // 设备类型
    MACAddress: string; // 设备mac地址
    services: Array<string>; // 服务列表
    techInfos: Array<string>; // 技术信息
}

export enum BLUETHOOTH_SERVICE {
    DOOR_BELL = "door.bell",
    SMART_BULB_SET = "smart.bulb.set",
    SMART_BULB_QUERY = "smart.bulb.query",
    SMART_LOCK_REGISTER = "smart.lock.register",
    SMART_LOCK_UNLOCK = "smart.lock.unlock",
    SMART_LOCK_LOCK = "smart.lock.lock",
    SMART_LOCK_AUTH = "smart.lock.auth",
    SMART_LOCK_REMOVE = "smart.lock.remove",
}


// 定义蓝牙主设备
export class BluetoothMasterDevice implements ISimulatorDevice, IHALDevice {
    // 增加接收消息回调函数
    protected onRecvMessage: Function;

    // 设备配置
    protected node: Node;

    // 设备配置
    protected MACAddress: string; // 设备mac地址
    protected services: Array<string>; // 服务列表
    protected techInfos: Array<string>; // 技术信息

    // 本地空口数组
    protected localAirPorts: Array<IHALInterface>;

    // 已配对设备
    protected pairedDeviceInfos: Array<BluetoothInfo>;

    // 桥接器
    protected bridge: any;

    // 记录事件
    protected addEventListener: Function;

    protected events: IOT_EVNET[] = [];

    protected getEventList: Function;

    // 构造函数
    constructor(node: Node, MACAddress: string, services: Array<string>, techInfos: Array<string>, onRecvMessage: Function, addEvent: Function, getEvent: Function) {
        this.node = node;

        // 保存配置
        this.MACAddress = MACAddress;
        this.services = services;
        this.techInfos = techInfos;

        // 默认不建立连接
        this.localAirPorts = [];
        this.pairedDeviceInfos = [];

        // 保存回调函数
        this.onRecvMessage = onRecvMessage;

        // 记录事件
        this.addEventListener = addEvent;

        this.getEventList = getEvent;

        bluethoothEnviroment.addMaster(this);
    }

    getNode(): Node<Node.Properties> {
        return this.node;
    }

    get deviceType() {
        return DeviceType.BluetoothMasterDevice;
    }

    get deviceConfig(): BluetoothInfo {
        return {
            deviceName: this.node.getAttrs().text.text as string,
            deviceType: this.deviceType,
            MACAddress: this.MACAddress,
            services: this.services,
            techInfos: this.techInfos,
        };
    }

    // 发送函数
    // 参数：已配对设备的BluetoothInfo，发送的数据
    // 返回：异步结果，发送成功与否
    // 如果设备MAC地址未配对，抛出异常
    // 如果发送成功，返回成功
    send(remoteDevice: BluetoothInfo, command: string, data: object | null = null, callback = console.log): void {
        // 查找远程设备序号
        const index = this.pairedDeviceInfos.findIndex((info) => {
            return info.MACAddress === remoteDevice.MACAddress;
        });

        // 未找到，直接返回
        if (index === -1) {
            throw new Error("未找到指定的设备");
        }

        // 组装消息
        const message = {
            service: command,
            ...data,
        };

        // 获取本地对应空口
        const localAirPort = this.localAirPorts[index];

        // 尝试发送数据
        localAirPort.HAL_Send(remoteDevice.MACAddress, HALFrameType.Bluetooth, message, (result: any) => {
            // 返回该参数收到的实际参数
            callback(result)
        });
    }

    // 连接函数，当前设备与目标设备建立空口
    // 配对设备
    connect(remoteDevice: BluetoothDevice): void {
        // 建立本地空口
        const localAirPort = new HALInterface(this, this.MACAddress);
        
        // 保存本地空口
        this.localAirPorts.push(localAirPort);

        // 通过远程设备连接，建立远程空口 -> 本地空口连接
        const remoteAirPort = remoteDevice.connect(this, localAirPort);

        // 建立本地空口 -> 远程空口连接
        localAirPort.link(remoteAirPort);

        // 保存连接上的远程设备
        const existDevice = this.pairedDeviceInfos.find((info) => {
            return info.MACAddress === remoteDevice.deviceConfig.MACAddress;
        });

        if (!existDevice) {
            this.pairedDeviceInfos.push(remoteDevice.deviceConfig);
        }

        // 刷新存储
        this.bridge.onGraphChange();
    }

    // 断开连接
    disconnect(remoteDevice: BluetoothDevice) {
        // 查找远程设备序号
        const index = this.pairedDeviceInfos.findIndex((info) => {
            return info.MACAddress === remoteDevice.deviceConfig.MACAddress;
        });

        // 未找到，直接返回
        if (index === -1) {
            return;
        }

        // 远程设备断开连接
        remoteDevice.disconnect();

        // 获取本地对应空口
        const localAirPort = this.localAirPorts[index];

        // 断开本地空口连接
        localAirPort.unlink();

        // 删除本地空口
        this.localAirPorts.splice(index, 1);

        // 删除连接上的远程设备
        this.pairedDeviceInfos.splice(index, 1);

        // 刷新存储
        this.bridge.onGraphChange();
    }

    // 使用MAC地址断开连接
    disconnectByMAC(MACAddress: string) {
        // 查找远程设备序号
        const index = this.pairedDeviceInfos.findIndex((info) => {
            return info.MACAddress === MACAddress;
        });

        // 未找到，直接返回
        if (index === -1) {
            return;
        }

        // 获取本地对应空口
        const localAirPort = this.localAirPorts[index];

        // 断开本地空口连接
        if (localAirPort) {
            localAirPort.unlink();
        }

        // 删除本地空口
        this.localAirPorts.splice(index, 1);

        // 删除连接上的远程设备
        this.pairedDeviceInfos.splice(index, 1);

        // 刷新存储
        this.bridge.onGraphChange();
    }


    // 断开全部连接
    disconnectAll() {
        // 断开所有连接
        this.pairedDeviceInfos.forEach((info) => {
            try {
                const device = bluethoothEnviroment.getDeviceByMAC(info.MACAddress);
                this.disconnect(device);
            }
            catch(e) {
                this.disconnectByMAC(info.MACAddress);
            }
        });
    }

    // 配置桥接器，用于变更通知
    setBridge(bridge: any): void {
        this.bridge = bridge;
    }

    // 此处只返回有线连接网口
    getPorts(): IHALInterface[] {
        return [];
    }

    destory() {
        // 断开配对设备
        this.disconnectAll();

        // 从环境中移除
        bluethoothEnviroment.removeMaster(this);
    }

    toJSON() {
        return {
            deviceType: DeviceType.BluetoothMasterDevice,
            MACAddress: this.MACAddress,
            services: this.services,
            techInfos: this.techInfos,
            pairedDeviceInfos: this.pairedDeviceInfos,
        };
    }

    fromJSON(config: any): void {
        this.MACAddress = config.MACAddress;
        this.services = config.services;
        this.techInfos = config.techInfos;
        this.pairedDeviceInfos = config.pairedDeviceInfos;
    }

    // 蓝牙设备需要实现的接口，例如接收控制指令
    HAL_Recv(fromInterface: IHALInterface, fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        this.onRecvMessage(fromInterface, fromMAC, toMAC, type, data, callback);
    }

    addEvent(level: IOT_EVENT_LEVEL ,message: string): void {
        // 无需实现
        this.addEventListener(level, message);
    }

    getEvents(): IOT_EVNET[] {
        return this.getEventList();
    }

    // 扫描设备
    scan(): Array<BluetoothDevice> {
        // 从环境中扫描可用设备
        const scanedDevices = bluethoothEnviroment.scan();

        // 过滤掉已配对设备
        const filteredDevices = scanedDevices.filter((device) => {
            return !this.pairedDeviceInfos.find((info) => {
                return info.MACAddress === device.deviceConfig.MACAddress;
            });
        });

        return filteredDevices;
    }

    // 只读已配对设备
    get pairedDeviceRecords(): Array<BluetoothInfo> {
        return this.pairedDeviceInfos;
    }
}

// 定义蓝牙终端设备
export class BluetoothDevice implements ISimulatorDevice, IHALDevice {
    // 设备节点
    protected node: Node;

    // 指示灯节点
    protected indicator: Node | null;

    // 配对的本地空口
    protected localAirPort: IHALInterface | null; 

    // 配对设备
    protected connectedDeviceMAC: string | null;

    // 桥接器
    protected bridge: any;

    // 是否处于配对状态
    protected isPairing: boolean = false;

    // 停用动画回调函数
    protected stopAnimationCallback: Function | null = null;

    // 设备配置
    protected MACAddress: string; // 设备mac地址
    protected services: Array<string>; // 服务列表
    protected techInfos: Array<string>; // 技术信息

    // 构造函数
    constructor(node: Node, MACAddress: string, services: Array<string>, techInfos: Array<string>) {
        // 保存配置
        this.MACAddress = MACAddress;
        this.services = services;
        this.techInfos = techInfos;

        // 默认不建立连接
        this.localAirPort = null;
        this.connectedDeviceMAC = null;

        // 节点
        this.node = node;
        this.indicator = null;

        // 加入蓝牙环境
        bluethoothEnviroment.add(this);
    }

    getNode(): Node<Node.Properties> {
        return this.node;
    }

    get deviceType() {
        return DeviceType.BluetoothDevice;
    }

    get deviceConfig(): BluetoothInfo {
        return {
            deviceName: this.node.getAttrs().text.text as string,
            deviceType: this.deviceType,
            MACAddress: this.MACAddress,
            services: this.services,
            techInfos: this.techInfos,
        }
    }

    // 获取配对状态
    get pairing(): boolean {
        return this.isPairing;
    }

    // 配置配对状态
    set pairing(value: boolean) {
        // 若已经处于配对状态，先断开连接
        if(this.isPairing) {
            this.disconnect(false);
        }

        this.isPairing = value;
        this.refreshGraph();
    }

    // 获取配对状态
    isPaired(): boolean {
        return this.connectedDeviceMAC !== null && this.localAirPort !== null;
    }

    // 发送函数
    // 参数：已配对设备的BluetoothInfo，发送的数据
    // 返回：异步结果，发送成功与否
    // 如果设备MAC地址未配对，抛出异常
    // 如果发送成功，返回成功
    send(command: string, data: object | null = null, callback = console.log): void {
        // 若尚未配对，抛出异常
        if (this.connectedDeviceMAC === null) {
            throw new Error('设备尚未配对');
        }

        // 组装消息
        const message = {
            service: command,
            ...data,
        };

        // 获取本地对应空口
        const localAirPort = this.localAirPort;
        if (!localAirPort) {
            throw new Error('无法找到对端蓝牙空口');
        }

        // 发送消息
        localAirPort.HAL_Send(this.connectedDeviceMAC, HALFrameType.Bluetooth, message, callback);
    }


    // 连接函数，当前设备与目标设备建立空口
    connect(remoteDevice: BluetoothMasterDevice, remoteAirPort: IHALInterface): IHALInterface {
        // 建立空口
        const localAirPort = new HALInterface(this, this.deviceConfig.MACAddress);

        // 关联空口
        localAirPort.link(remoteAirPort);

        // 维护本地配对记录
        this.localAirPort = localAirPort;
        this.connectedDeviceMAC = remoteDevice.deviceConfig.MACAddress;

        // 停止配对
        this.isPairing = false;

        // 刷新图形
        this.refreshGraph();

        // 返回本地空口
        return localAirPort;
    }

    // 恢复连接函数
    restore() {
        // 获取原有空口对端MAC
        const connectedMac = this.connectedDeviceMAC;
        if (!connectedMac) {
            // throw new Error('无法找到对端蓝牙空口');
            return;
        }

        // 获取对端设备
        const remoteDevice = bluethoothEnviroment.getMasterByMAC(connectedMac);

        // 恢复连接
        remoteDevice.connect(this);

        // 刷新图形
        this.refreshGraph();
    }

    // 断开连接
    disconnect(refreshGraph = true) {
        // 如果有端口
        if (this.localAirPort) {
            // 断开连接
            this.localAirPort.unlink();

            // 清空本地记录
            this.localAirPort = null;
        }
        
        // 如果有设备
        if (this.connectedDeviceMAC) {
            // 清空本地记录
            this.connectedDeviceMAC = null;
        }

        // 刷新图形
        if(refreshGraph) {
            this.refreshGraph();
        }
    }

    getIndicatorOffset() {
        return {
            x: 30,
            y: 42,
        }
    }

    // 配置桥接器，用于变更通知
    setBridge(bridge: any): void {
        this.bridge = bridge;

        const { node } = this;

        if(node.getChildCount() === 0) {
            const position = node.getPosition();
            const offset = this.getIndicatorOffset();
            this.indicator = bridge.addNode({
                shape: 'IndicatorLight',
                x: position.x + offset.x,
                y: position.y + offset.y,
                data: {
                    disableMove: true,
                }
            })
    
            // 补充蓝牙图标
            node.addChild(this.indicator);
        }
        else {
            this.indicator = node.getChildAt(0) as Node;
        }
    }

    // 此处只返回有线连接网口
    getPorts(): IHALInterface[] {
        return [];
    }

    destory() {
        // 断开配对设备
        this.disconnect();

        // 从蓝牙环境中移除
        bluethoothEnviroment.remove(this);
    }

    toJSON() {
        return {
            deviceType: DeviceType.BluetoothDevice,
            MACAddress: this.MACAddress,
            services: this.services,
            techInfos: this.techInfos,
            connectedDeviceMAC: this.connectedDeviceMAC,
            isPairing: this.isPairing,
        };
    }

    fromJSON(config: any): void {
        this.MACAddress = config.MACAddress;
        this.services = config.services;
        this.techInfos = config.techInfos;
        this.connectedDeviceMAC = config.connectedDeviceMAC;
        this.isPairing = config.isPairing;
    }

    // 蓝牙设备需要实现的接口，例如接收控制指令
    HAL_Recv(fromInterface: IHALInterface, fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        throw new Error("Method not implemented.");
    }

    // 刷新蓝牙连接状态显示
    refreshGraph() {
        // 如果没有桥接器或者没有指示灯，则不刷新
        if(!this.bridge || !this.indicator) {
            return;
        }

        // 获取指示灯视图
        const view = this.bridge.findView(this.indicator);
        if(!view) {
            return;
        }

        // 若正在动画，则停止动画
        if(this.stopAnimationCallback) {
            this.stopAnimationCallback();
            this.stopAnimationCallback = null;
        }

        // 如果正在配对，则指示灯蓝色闪烁
        if (this.isPairing) {
            this.stopAnimationCallback = view.animate('circle', {
                attributeType: 'CSS',
                attributeName: 'fill',
                from: '#4685FA',
                to: '#fff',
                dur: '1s',
                repeatCount: 'indefinite',
            });

            return;
        }

        // 如果已经配对且已经连接到主设备，则指示灯蓝色常亮
        if (this.connectedDeviceMAC) {
            this.stopAnimationCallback = view.animate('circle', {
                attributeType: 'CSS',
                attributeName: 'fill',
                from: '#4685FA',
                to: '#4685FA',                
                dur: '1s',
                repeatCount: 'indefinite',
            });

            return;
        }

        // 否则指示灯为红色
        this.stopAnimationCallback = view.animate('circle', {
            attributeType: 'CSS',
            attributeName: 'fill',
            from: 'red',
            to: 'red',                  
            dur: '1s',
            repeatCount: 'indefinite',
        });
    }
}

// 蓝牙环境
//   提供扫描函数，返回蓝牙设备列表
class BluethoothEnviroment {
    protected masters: Array<BluetoothMasterDevice> = [];
    protected devices: Array<BluetoothDevice> = [];

    // 安装蓝牙主机
    addMaster(master: BluetoothMasterDevice) {
        this.masters.push(master);
    }

    // 卸载蓝牙主机
    removeMaster(master: BluetoothMasterDevice) {
        this.masters.splice(this.masters.indexOf(master), 1);
    }

    // 根据MAC地址获取主机
    getMasterByMAC(MAC: string): BluetoothMasterDevice {
        const master = this.masters.find(master => master.deviceConfig.MACAddress === MAC);
        if (!master) {
            throw new Error('没有找到对应的设备');
        }

        return master;
    }

    // 安装蓝牙设备
    add(device: BluetoothDevice) {
        this.devices.push(device);
    }

    // 卸载蓝牙设备
    remove(device: BluetoothDevice) {
        this.devices.splice(this.devices.indexOf(device), 1);
    }

    // 扫描函数，返回配对状态的蓝牙设备列表
    scan(): Array<BluetoothDevice> {
        return this.devices.filter(device => device.pairing);
    }

    // 根据MAC地址获取设备
    getDeviceByMAC(MAC: string): BluetoothDevice {
        const device = this.devices.find(device => device.deviceConfig.MACAddress === MAC);
        if (!device) {
            throw new Error('没有找到对应的设备');
        }

        return device;
    }
}

export const bluethoothEnviroment = new BluethoothEnviroment();